import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Define skin configurations - maps skin IDs to their visual effects
// Must match Battle Pass reward IDs
export const SKIN_CONFIGS = {
  golden_banana: {
    appliesTo: 'banana',
    emoji: '🌟',
    glow: 'golden',
    animation: 'shimmer',
    cssClass: 'skin-golden-banana'
  },
  crystal_apple: {
    appliesTo: 'apple',
    emoji: '💎',
    glow: 'crystal',
    animation: 'sparkle',
    cssClass: 'skin-crystal-apple'
  },
  neon_orange: {
    appliesTo: 'orange',
    emoji: '✨',
    glow: 'neon',
    animation: 'pulse-neon',
    cssClass: 'skin-neon-orange'
  },
  diamond_grape: {
    appliesTo: 'blueberry',
    emoji: '💠',
    glow: 'diamond',
    animation: 'shine',
    cssClass: 'skin-diamond-grape'
  },
  ruby_cherry: {
    appliesTo: 'cherry',
    emoji: '❤️',
    glow: 'ruby',
    animation: 'pulse-ruby',
    cssClass: 'skin-ruby-cherry'
  },
  plasma_pear: {
    appliesTo: 'pear',
    emoji: '⚡',
    glow: 'plasma',
    animation: 'electric',
    cssClass: 'skin-plasma-pear'
  },
  cosmic_melon: {
    appliesTo: 'watermelon',
    emoji: '🌌',
    glow: 'cosmic',
    animation: 'cosmic-pulse',
    cssClass: 'skin-cosmic-melon'
  },
  inferno_strawberry: {
    appliesTo: 'strawberry',
    emoji: '🔥',
    glow: 'fire',
    animation: 'flame',
    cssClass: 'skin-inferno-strawberry'
  },
  legendary_fruit_set: {
    appliesTo: 'all', // Affects all fruits
    glow: 'legendary',
    animation: 'legendary-glow',
    cssClass: 'skin-legendary-set'
  }
};

// Define theme configurations - must match Battle Pass reward IDs
export const THEME_CONFIGS = {
  galaxy: {
    id: 'galaxy',
    boardBackground: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    cellBackground: 'rgba(147, 112, 219, 0.2)',
    borderColor: '#9370db',
    cssClass: 'theme-galaxy'
  },
  underwater: {
    id: 'underwater',
    boardBackground: 'linear-gradient(135deg, #0077b6 0%, #00a8e8 50%, #90e0ef 100%)',
    cellBackground: 'rgba(0, 180, 216, 0.2)',
    borderColor: '#00b4d8',
    cssClass: 'theme-underwater'
  },
  candy_land: {
    id: 'candy_land',
    boardBackground: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 50%, #ffecd2 100%)',
    cellBackground: 'rgba(255, 192, 203, 0.3)',
    borderColor: '#ff69b4',
    cssClass: 'theme-candy-land'
  },
  neon_city: {
    id: 'neon_city',
    boardBackground: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 50%, #16213e 100%)',
    cellBackground: 'rgba(0, 255, 255, 0.1)',
    borderColor: '#00ffff',
    cssClass: 'theme-neon-city'
  },
  royal_palace: {
    id: 'royal_palace',
    boardBackground: 'linear-gradient(135deg, #3d0066 0%, #6b0099 50%, #9900cc 100%)',
    cellBackground: 'rgba(153, 0, 204, 0.2)',
    borderColor: '#cc00ff',
    cssClass: 'theme-royal-palace'
  }
};

// Define effect configurations - must match Battle Pass reward IDs
export const EFFECT_CONFIGS = {
  sparkle_trail: {
    id: 'sparkle_trail',
    particleEmoji: '✨',
    particleCount: 8,
    cssClass: 'effect-sparkle',
    explosionClass: 'explosion-sparkle'
  },
  fire_match: {
    id: 'fire_match',
    particleEmoji: '🔥',
    particleCount: 6,
    cssClass: 'effect-fire',
    explosionClass: 'explosion-fire'
  },
  rainbow_burst: {
    id: 'rainbow_burst',
    particleEmoji: '🌈',
    particleCount: 6,
    cssClass: 'effect-rainbow',
    explosionClass: 'explosion-rainbow'
  },
  lightning_strike: {
    id: 'lightning_strike',
    particleEmoji: '⚡',
    particleCount: 10,
    cssClass: 'effect-lightning',
    explosionClass: 'explosion-lightning'
  },
  galaxy_swirl: {
    id: 'galaxy_swirl',
    particleEmoji: '⭐',
    particleCount: 10,
    cssClass: 'effect-cosmic',
    explosionClass: 'explosion-cosmic'
  }
};

// Context for cosmetics
const CosmeticsContext = createContext(null);

export function CosmeticsProvider({ children }) {
  const [cosmetics, setCosmetics] = useState({
    unlockedSkins: [],
    unlockedEffects: [],
    unlockedThemes: [],
    unlockedFrames: [],
    equippedSkin: null,
    equippedEffect: null,
    equippedTheme: null,
    equippedFrame: null,
    isPremium: false, // Track premium status
  });
  const [loading, setLoading] = useState(true);

  // Fetch cosmetics from backend
  const fetchCosmetics = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      console.log('[useCosmetics] No token found, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const battlePass = data.battle_pass || {};
        const isPremium = battlePass.is_premium || false;
        
        console.log('[useCosmetics] Fetched cosmetics data:', {
          equipped_skin: data.equipped_skin,
          equipped_theme: data.equipped_theme,
          equipped_effect: data.equipped_effect,
          is_premium: isPremium
        });
        
        // Only apply cosmetics if user has premium
        setCosmetics({
          unlockedSkins: data.unlocked_skins || [],
          unlockedEffects: data.unlocked_effects || [],
          unlockedThemes: data.unlocked_themes || [],
          unlockedFrames: data.unlocked_frames || [],
          // Only set equipped cosmetics if user is premium
          equippedSkin: isPremium ? (data.equipped_skin || null) : null,
          equippedEffect: isPremium ? (data.equipped_effect || null) : null,
          equippedTheme: isPremium ? (data.equipped_theme || null) : null,
          equippedFrame: isPremium ? (data.equipped_frame || null) : null,
          isPremium: isPremium,
        });
      }
    } catch (error) {
      console.error('Failed to fetch cosmetics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and when token changes
  useEffect(() => {
    fetchCosmetics();
    
    // Listen for storage events (login/logout in other tabs)
    const handleStorageChange = (e) => {
      if (e.key === TOKEN_KEY) {
        console.log('[useCosmetics] Token changed, re-fetching cosmetics');
        fetchCosmetics();
      }
    };
    
    // Also check periodically for token changes (same tab login)
    const checkToken = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token && cosmetics.equippedSkin === null && cosmetics.equippedTheme === null) {
        console.log('[useCosmetics] Token found but no cosmetics loaded, fetching...');
        fetchCosmetics();
      }
    };
    
    // Check every 2 seconds for token changes
    const interval = setInterval(checkToken, 2000);
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [fetchCosmetics, cosmetics.equippedSkin, cosmetics.equippedTheme]);

  // Equip cosmetic
  const equipCosmetic = useCallback(async (type, id) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;

    // Check if user is premium before attempting to equip
    if (!cosmetics.isPremium && id) {
      console.warn('[useCosmetics] Cannot equip cosmetics - Premium Battle Pass required');
      return { success: false, error: 'Premium Battle Pass required to use cosmetics' };
    }

    try {
      const response = await fetch(`${API_URL}/api/cosmetics/equip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cosmetic_type: type.replace(/s$/, ''), // Remove trailing 's'
          cosmetic_id: id
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Update local state
        setCosmetics(prev => {
          const key = `equipped${type.charAt(0).toUpperCase() + type.slice(1, -1)}`;
          return { ...prev, [key]: id };
        });
        return { success: true };
      } else {
        return { success: false, error: data.detail || 'Failed to equip cosmetic' };
      }
    } catch (error) {
      console.error('Failed to equip cosmetic:', error);
      return { success: false, error: 'Network error' };
    }
  }, [cosmetics.isPremium]);

  // Get skin config for a fruit type
  const getSkinForFruit = useCallback((fruitType) => {
    const { equippedSkin } = cosmetics;
    if (!equippedSkin) return null;

    const skinConfig = SKIN_CONFIGS[equippedSkin];
    if (!skinConfig) return null;

    // Check if this skin applies to this fruit or all fruits
    if (skinConfig.appliesTo === 'all' || skinConfig.appliesTo === fruitType) {
      return skinConfig;
    }

    return null;
  }, [cosmetics]);

  // Get current theme config
  const getCurrentTheme = useCallback(() => {
    const { equippedTheme } = cosmetics;
    if (!equippedTheme) return null;
    return THEME_CONFIGS[equippedTheme] || null;
  }, [cosmetics]);

  // Get current effect config
  const getCurrentEffect = useCallback(() => {
    const { equippedEffect } = cosmetics;
    if (!equippedEffect) return null;
    return EFFECT_CONFIGS[equippedEffect] || null;
  }, [cosmetics]);

  const value = {
    ...cosmetics,
    loading,
    fetchCosmetics,
    equipCosmetic,
    getSkinForFruit,
    getCurrentTheme,
    getCurrentEffect,
    SKIN_CONFIGS,
    THEME_CONFIGS,
    EFFECT_CONFIGS
  };

  return (
    <CosmeticsContext.Provider value={value}>
      {children}
    </CosmeticsContext.Provider>
  );
}

export function useCosmetics() {
  const context = useContext(CosmeticsContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      unlockedSkins: [],
      unlockedEffects: [],
      unlockedThemes: [],
      unlockedFrames: [],
      equippedSkin: null,
      equippedEffect: null,
      equippedTheme: null,
      equippedFrame: null,
      isPremium: false,
      loading: false,
      fetchCosmetics: () => {},
      equipCosmetic: () => false,
      getSkinForFruit: () => null,
      getCurrentTheme: () => null,
      getCurrentEffect: () => null,
      SKIN_CONFIGS,
      THEME_CONFIGS,
      EFFECT_CONFIGS
    };
  }
  return context;
}
